import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { reviewApi, type SessionRow } from "../../../api/clock";
import { venueApi } from "../../../api/resources";
import type { VenueAdmin } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { Modal } from "../../../components/Overlay";
import { Button, ErrorText, StatusPill, TextInput } from "../../../components/ui";
import { DataTable, LoadState, useLoad } from "../shared";

const sel = "h-10 rounded-sm border border-white/15 bg-surface px-3 text-xs text-white";
const iso = (d: Date) => d.toISOString().slice(0, 10);

export function EditModal({ row, onClose, onSaved }: { row: SessionRow; onClose: () => void; onSaved: () => void }) {
  const [pin, setPin] = useState(row.payable_in_local ?? "");
  const [pout, setPout] = useState(row.payable_out_local ?? "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await reviewApi.editPayable(row.id, { payable_in_local: pin || null, payable_out_local: pout || null, reason });
      toast.success(row.payable_approved ? "Saved, and the session is unapproved" : "Payable time saved");
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open title="Edit payable time" onClose={onClose} testId="payable-edit-modal">
      <div className="space-y-3 text-sm">
        <p className="text-xs text-zinc-400">{row.employee_name} · {row.venue_name} · {row.work_date}. Times are venue-local. Original evidence is never changed.</p>
        <dl data-testid="payable-original-values" className="grid grid-cols-3 gap-1 rounded-sm bg-ink p-3 text-xs">
          <dt /><dt className="text-zinc-500">Original</dt><dt className="text-zinc-500">Current</dt>
          <dt className="text-zinc-500">In</dt><dd>{row.original_in ?? row.payable_in ?? "—"}</dd><dd>{row.payable_in || "—"}</dd>
          <dt className="text-zinc-500">Out</dt><dd>{row.original_out ?? row.payable_out ?? "—"}</dd><dd>{row.payable_out || "—"}</dd>
        </dl>
        <label className="block text-xs text-zinc-400">Payable in<TextInput type="datetime-local" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="payable-edit-in" /></label>
        <label className="block text-xs text-zinc-400">Payable out (overnight allowed)<TextInput type="datetime-local" value={pout} onChange={(e) => setPout(e.target.value)} data-testid="payable-edit-out" /></label>
        <label className="block text-xs text-zinc-400">Reason (required)<textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1 w-full rounded-sm border border-white/15 bg-surface p-2 text-sm text-white" data-testid="payable-edit-reason" /></label>
        {row.payable_approved && <p className="text-xs text-warn">This session is approved. Saving will un-approve it.</p>}
        <ErrorText message={error} testId="payable-edit-error" />
        <Button onClick={save} loading={busy} disabled={reason.trim().length < 3} data-testid="payable-edit-save">Save</Button>
      </div>
    </Modal>
  );
}

export default function AdminSessions() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ date_from: iso(new Date(Date.now() - 14 * 864e5)), date_to: iso(new Date(Date.now() + 864e5)), venue_id: "", status: "", employee_id: params.get("employee_id") ?? "" });
  const [reload, setReload] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [edit, setEdit] = useState<SessionRow | null>(null);
  const [venues, setVenues] = useState<VenueAdmin[]>([]);
  useEffect(() => { venueApi.list().then(setVenues).catch(() => setVenues([])); }, []);
  const loader = useCallback(() => reviewApi.sessions(f), [f, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<SessionRow[]>(loader);
  const bump = () => { setReload((r) => r + 1); setPicked(new Set()); };
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast.success(ok); bump(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const bulk = () => run(async () => {
    const r = await reviewApi.approveBulk([...picked]);
    const bad = r.results.filter((x) => !x.ok);
    if (bad.length) toast.warning(`${bad.length} not approved: ${bad[0].message}`);
  }, "Bulk approval done");
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <section data-testid="admin-sessions-page" className="animate-rise">
      <PageHeader title="Session review">
        <Button onClick={bulk} disabled={!picked.size} data-testid="sessions-bulk-approve-button"><CheckCheck className="h-4 w-4" /> Approve selected ({picked.size})</Button>
      </PageHeader>
      <p className="mb-4 text-xs text-zinc-500">Payable times only. No pay amounts are calculated. Duration is informational.</p>
      <div className="mb-4 flex flex-wrap gap-2">
        <input type="date" value={f.date_from} onChange={set("date_from")} className={sel} data-testid="sessions-filter-from" />
        <input type="date" value={f.date_to} onChange={set("date_to")} className={sel} data-testid="sessions-filter-to" />
        <select value={f.venue_id} onChange={set("venue_id")} className={sel} data-testid="sessions-filter-venue"><option value="">All venues</option>{venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
        <select value={f.status} onChange={set("status")} className={sel} data-testid="sessions-filter-status"><option value="">All statuses</option>{["CLOCKED_IN", "PENDING_CLOCK_OUT_GPS", "COMPLETED", "NEEDS_REVIEW"].map((s) => <option key={s}>{s}</option>)}</select>
        {f.employee_id && <button type="button" onClick={() => setF((x) => ({ ...x, employee_id: "" }))} className="text-xs text-signal" data-testid="sessions-clear-employee">Clear employee filter ×</button>}
      </div>
      <LoadState error={error} loading={!data && !error} testId="sessions-load-state" />
      {data && (
        <DataTable testId="sessions-table" headers={["", "Date", "Employee", "Venue", "Status", "IN text / GPS", "OUT text / GPS", "Payable in → out", "Duration", "Approved", ""]} empty={data.length === 0}>
          {data.map((r) => (
            <tr key={r.id} data-testid={`session-row-${r.id}`} className="text-xs text-zinc-300">
              <td className="px-3 py-3"><input type="checkbox" checked={picked.has(r.id)} disabled={!!r.approve_blocker || r.payable_approved} onChange={() => toggle(r.id)} data-testid={`session-select-${r.id}`} /></td>
              <td className="px-3 py-3">{r.work_date}</td><td className="px-3 py-3 text-white">{r.employee_name}</td><td className="px-3 py-3">{r.venue_name}</td>
              <td className="px-3 py-3"><StatusPill status={r.status} />{r.review_flags.length > 0 && <p className="mt-1 font-mono text-[10px] text-warn">{r.review_flags.join(", ")}</p>}</td>
              <td className="px-3 py-3">{r.in_sms || "—"}<br /><span className="text-zinc-500">{r.in_gps || "—"}</span></td>
              <td className="px-3 py-3">{r.out_sms || "—"}<br /><span className="text-zinc-500">{r.out_gps || "—"}</span></td>
              <td className="px-3 py-3" data-testid={`session-payable-${r.id}`}>{r.payable_in || "—"} → {r.payable_out || "—"}{r.edited && <p className="text-[10px] text-sky-300">edited · was {r.original_in ?? "—"} → {r.original_out ?? "—"}</p>}</td>
              <td className="px-3 py-3 font-mono">{r.duration ?? "—"}</td>
              <td className="px-3 py-3" data-testid={`session-approved-${r.id}`}>{r.payable_approved ? <span className="rounded-sm bg-emerald-400/15 px-2 py-0.5 text-[10px] font-bold text-emerald-300">APPROVED</span> : <span className="text-zinc-500" title={r.approve_blocker ?? ""}>{r.approve_blocker ? "Blocked" : "No"}</span>}</td>
              <td className="space-x-2 whitespace-nowrap px-3 py-3">
                <button type="button" onClick={() => setEdit(r)} className="text-signal hover:text-white" data-testid={`session-edit-${r.id}`}>Edit</button>
                {!r.payable_approved && <button type="button" disabled={!!r.approve_blocker} title={r.approve_blocker ?? ""} onClick={() => run(() => reviewApi.approve(r.id), "Payable time approved")} className="text-emerald-300 hover:text-white disabled:opacity-30" data-testid={`session-approve-${r.id}`}>Approve</button>}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
      {edit && <EditModal row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); bump(); }} />}
    </section>
  );
}
